/**
 * Hook to check Apple Pay availability on the web
 *
 * Supports both Safari (native ApplePaySession) and third-party browsers
 * (Chrome, Firefox, Edge) via iOS 18+ QR code flow using applePayCapabilities().
 *
 * @see https://developer.apple.com/documentation/applepayontheweb/apple-pay-js-api
 * @see https://developer.apple.com/documentation/apple_pay_on_the_web/applepaycapabilities
 */
import {useCallback, useEffect, useState} from 'react';
import {Platform} from 'react-native';

import {logger} from '../../../../../utils/logger';

import type {ApplePayAvailability, ApplePayCredentialStatus} from './types';

/**
 * Check if Apple Pay SDK is available.
 * SDK is loaded via index.html <script> tag - no dynamic loading needed.
 */
function isApplePaySDKLoaded(): boolean {
  return typeof window !== 'undefined' && typeof window.ApplePaySession !== 'undefined';
}

interface UseApplePayAvailabilityOptions {
  /** Whether to skip the check (useful for native platforms) */
  skip?: boolean;
}

interface UseApplePayAvailabilityResult extends ApplePayAvailability {
  /** Re-check availability (e.g., after user adds a card) */
  recheck: () => Promise<void>;
  /** Whether availability check is in progress */
  isChecking: boolean;
  /** Whether this is a third-party browser using QR code flow */
  isThirdPartyBrowser: boolean;
}

/**
 * Check if Apple Pay is available on the current device/browser.
 *
 * Flow for Safari:
 * 1. Check if window.ApplePaySession exists
 * 2. Check ApplePaySession.canMakePayments() for device capability
 * 3. Optionally check canMakePaymentsWithActiveCard() for active cards
 *
 * Flow for third-party browsers (Chrome, Firefox, Edge) with iOS 18+:
 * 1. Check if applePayCapabilities() is available (SDK 1.2.0+)
 * 2. Call applePayCapabilities() to get payment credential status
 * 3. Map status to availability flags
 *
 * @example
 * ```tsx
 * const {isSupported, canMakePayments, isThirdPartyBrowser} = useApplePayAvailability();
 *
 * if (!isSupported) return null;
 * // Button shown per Apple's guidance: "If canMakePayments() returns true, show the button"
 * // Third-party browsers (Chrome/Firefox/Edge) will show QR code flow for iOS 18+ users
 * return <ApplePayButton />;
 * ```
 */
export function useApplePayAvailability(
  options: UseApplePayAvailabilityOptions = {},
): UseApplePayAvailabilityResult {
  const {skip = false} = options;

  const [availability, setAvailability] = useState<ApplePayAvailability>({
    isSupported: false,
    canMakePayments: false,
    canMakePaymentsWithActiveCard: false,
  });
  const [isChecking, setIsChecking] = useState(false);
  const [isThirdPartyBrowser, setIsThirdPartyBrowser] = useState(false);

  const checkAvailability = useCallback(async () => {
    // Only check on web
    if (Platform.OS !== 'web' || skip) {
      setAvailability({
        isSupported: false,
        canMakePayments: false,
        canMakePaymentsWithActiveCard: false,
        error: Platform.OS !== 'web' ? 'Apple Pay JS only available on web' : undefined,
      });
      setIsThirdPartyBrowser(false);
      return;
    }

    setIsChecking(true);

    try {
      // Apple Pay requires actual HTTPS (not just "secure context" - localhost won't work)
      const isActualHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';

      if (!isActualHttps) {
        logger.warn(
          'useApplePayAvailability',
          'Apple Pay requires HTTPS - skipping on non-HTTPS origin',
          {
            protocol: window?.location?.protocol,
            hostname: window?.location?.hostname,
          },
        );
        setAvailability({
          isSupported: false,
          canMakePayments: false,
          canMakePaymentsWithActiveCard: false,
          error: 'Apple Pay requires HTTPS',
        });
        setIsThirdPartyBrowser(false);
        setIsChecking(false);
        return;
      }

      // SDK loaded via index.html - just verify it's available
      if (!isApplePaySDKLoaded()) {
        logger.warn('useApplePayAvailability', 'Apple Pay SDK not loaded - check index.html');
        setAvailability({
          isSupported: false,
          canMakePayments: false,
          canMakePaymentsWithActiveCard: false,
          error: 'Apple Pay SDK not available',
        });
        setIsThirdPartyBrowser(false);
        setIsChecking(false);
        return;
      }

      // SDK is available - check if ApplePaySession is available
      // The SDK provides ApplePaySession in ALL browsers (Safari + Chrome/Firefox/Edge)
      if (typeof window === 'undefined' || !window.ApplePaySession) {
        setAvailability({
          isSupported: false,
          canMakePayments: false,
          canMakePaymentsWithActiveCard: false,
          error: 'Apple Pay is not supported in this browser',
        });
        setIsThirdPartyBrowser(false);
        setIsChecking(false);
        return;
      }

      // Check canMakePayments() first - per Apple WWDC24:
      // "If canMakePayments() returns true, the Apple Pay button MUST always be shown"
      // After SDK load, this returns true in third-party browsers for QR code flow
      const canMakePaymentsExists = typeof ApplePaySession.canMakePayments === 'function';
      const canMakePaymentsResult = canMakePaymentsExists
        ? ApplePaySession.canMakePayments()
        : false;

      logger.info('useApplePayAvailability', 'canMakePayments check', {
        canMakePaymentsExists,
        canMakePaymentsResult,
        hasApplePaySession: !!window.ApplePaySession,
      });

      // If canMakePayments() returns true, show the button (Apple's rule)
      // This covers both Safari native flow AND third-party browser QR code flow
      if (canMakePaymentsResult) {
        // Now check applePayCapabilities() for more details (optional but informative)
        const hasCapabilitiesAPI = typeof ApplePaySession.applePayCapabilities === 'function';
        let credentialStatus: ApplePayCredentialStatus | undefined;
        let canMakePaymentsWithActiveCard = false;

        if (hasCapabilitiesAPI) {
          try {
            const capabilities = await ApplePaySession.applePayCapabilities!();
            credentialStatus = capabilities.paymentCredentialStatus as ApplePayCredentialStatus;

            logger.info('useApplePayAvailability', 'applePayCapabilities() result', {
              status: credentialStatus,
            });

            // Determine active card status from credential status
            canMakePaymentsWithActiveCard = credentialStatus === 'paymentCredentialsAvailable';
          } catch (capabilitiesError) {
            logger.warn('useApplePayAvailability', 'applePayCapabilities() failed', {
              error:
                capabilitiesError instanceof Error
                  ? capabilitiesError.message
                  : String(capabilitiesError),
            });
          }
        }

        // Note: canMakePaymentsWithActiveCard(merchantId) is legacy and no longer needed
        // Per Apple WWDC24: "If canMakePayments() returns true, show the button"
        // The applePayCapabilities() API above provides credential status without merchantId

        // Detect third-party browser: has SDK but not native Safari Apple Pay
        // In Safari, canMakePayments returns true natively
        // In Chrome/Firefox/Edge, SDK provides canMakePayments but via QR code flow
        const isSafariBrowser =
          typeof navigator !== 'undefined' &&
          /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const isThirdParty = !isSafariBrowser;
        setIsThirdPartyBrowser(isThirdParty);

        setAvailability({
          isSupported: true,
          canMakePayments: true,
          canMakePaymentsWithActiveCard,
          paymentCredentialStatus: credentialStatus,
        });

        logger.info('useApplePayAvailability', 'Apple Pay available', {
          isSupported: true,
          canMakePayments: true,
          canMakePaymentsWithActiveCard,
          isThirdPartyBrowser: isThirdParty,
          credentialStatus,
        });

        setIsChecking(false);
        return;
      }

      // canMakePayments() returned false - try applePayCapabilities() as fallback
      // This handles edge cases where SDK loaded but canMakePayments is false
      const hasCapabilitiesAPI = typeof ApplePaySession.applePayCapabilities === 'function';

      if (hasCapabilitiesAPI) {
        try {
          const capabilities = await ApplePaySession.applePayCapabilities!();
          const status = capabilities.paymentCredentialStatus as ApplePayCredentialStatus;

          logger.info('useApplePayAvailability', 'applePayCapabilities() fallback result', {
            status,
            canMakePaymentsResult,
          });

          // Per WWDC24: Only hide button when status is explicitly 'applePayUnsupported'
          // AND canMakePayments is also false
          // For 'paymentCredentialStatusUnknown' - show button (QR code flow)
          // For 'paymentCredentialsUnavailable' - show button (user can set up)
          if (status !== 'applePayUnsupported') {
            // Show button - QR code flow will be available
            setIsThirdPartyBrowser(true);
            setAvailability({
              isSupported: true,
              canMakePayments: true,
              canMakePaymentsWithActiveCard: status === 'paymentCredentialsAvailable',
              paymentCredentialStatus: status,
            });

            logger.info('useApplePayAvailability', 'Apple Pay available via capabilities API', {
              status,
              isThirdPartyBrowser: true,
            });

            setIsChecking(false);
            return;
          }

          // applePayUnsupported - don't show button
          setAvailability({
            isSupported: false,
            canMakePayments: false,
            canMakePaymentsWithActiveCard: false,
            paymentCredentialStatus: status,
            error: 'Apple Pay is not supported on this device',
          });
          setIsThirdPartyBrowser(false);
          setIsChecking(false);
          return;
        } catch (capabilitiesError) {
          logger.warn('useApplePayAvailability', 'applePayCapabilities() fallback failed', {
            error:
              capabilitiesError instanceof Error
                ? capabilitiesError.message
                : String(capabilitiesError),
          });
        }
      }

      // Both canMakePayments() and applePayCapabilities() failed/returned false
      // This is a genuine "not supported" case
      setAvailability({
        isSupported: false,
        canMakePayments: false,
        canMakePaymentsWithActiveCard: false,
        error: 'Apple Pay is not available',
      });
      setIsThirdPartyBrowser(false);

      logger.info('useApplePayAvailability', 'Apple Pay not available', {
        canMakePaymentsResult,
        hasCapabilitiesAPI,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      logger.error('useApplePayAvailability', 'Error checking availability', {error: errorMessage});

      setAvailability({
        isSupported: false,
        canMakePayments: false,
        canMakePaymentsWithActiveCard: false,
        error: errorMessage,
      });
      setIsThirdPartyBrowser(false);
    } finally {
      setIsChecking(false);
    }
  }, [skip]);

  useEffect(() => {
    checkAvailability();
  }, [checkAvailability]);

  return {
    ...availability,
    recheck: checkAvailability,
    isChecking,
    isThirdPartyBrowser,
  };
}

export default useApplePayAvailability;
